// SPDX-FileCopyrightText: 2026 Ebiose AI
// SPDX-License-Identifier: LicenseRef-Ebiose-Proprietary
// All rights reserved. Confidential — unauthorized distribution prohibited.

import type { SortingState, VisibilityState } from "@tanstack/react-table"

// The sort is single-key by design: the table disables multi-sort, so a
// second key in the URL would be announced (header arrow, shareable link)
// without ever ordering rows. Encode emits the first key only; decode
// collapses a hand-edited comma list onto its first valid token.
export function encodeSort(sorting: SortingState): string {
  const active = sorting[0]
  if (!active) return ""
  return active.desc ? `-${active.id}` : active.id
}

export function decodeSort(
  param: string | null | undefined,
  validColumnIds: readonly string[],
): SortingState {
  if (!param) return []
  const valid = new Set(validColumnIds)
  const active = param
    .split(",")
    .map((token) => token.trim())
    .filter((token) => token.length > 0)
    .map((token) =>
      token.startsWith("-")
        ? { id: token.slice(1), desc: true }
        : { id: token, desc: false },
    )
    .find((sort) => valid.has(sort.id))
  return active ? [active] : []
}

export function encodePage(pageIndex: number): string {
  const safe = Number.isFinite(pageIndex) ? Math.max(0, Math.floor(pageIndex)) : 0
  return String(safe + 1)
}

export function decodePage(param: string | null | undefined): number {
  const parsed = Number(param)
  if (!Number.isInteger(parsed) || parsed < 1) return 0
  return parsed - 1
}

export function clampPage(pageIndex: number, pageCount: number): number {
  if (!Number.isFinite(pageIndex) || pageIndex < 0) return 0
  const lastPage = Math.max(0, Math.floor(pageCount) - 1)
  return Math.min(Math.floor(pageIndex), lastPage)
}

// Sentinel for an explicit "no columns hidden" choice. It keeps the param
// present-but-empty of ids so the reader can tell "user revealed everything"
// (round-trips to all-visible) from an absent param (restores defaultHidden).
// Not a valid column id, so decodeVisibility maps it to the empty hidden-set.
export const VISIBILITY_NONE_HIDDEN = "-"

export function encodeVisibility(visibility: VisibilityState): string {
  const hidden = Object.entries(visibility)
    .filter(([, visible]) => visible === false)
    .map(([id]) => id)
  return hidden.length > 0 ? hidden.join(",") : VISIBILITY_NONE_HIDDEN
}

export function decodeVisibility(
  param: string | null | undefined,
  validColumnIds: readonly string[],
): VisibilityState {
  if (!param) return {}
  const valid = new Set(validColumnIds)
  const visibility: VisibilityState = {}
  for (const token of param.split(",")) {
    const id = token.trim()
    if (id.length > 0 && valid.has(id)) visibility[id] = false
  }
  return visibility
}
